import StudioPage from "@/pages/StudioPage";

const P = ({ testid, kicker, title, centered = false, children }) => (
  <StudioPage testid={testid} kicker={kicker} title={title} centered={centered} body={children} />
);

export function About() {
  return (
    <P testid="page-about" kicker="About" title="Resilience Multimedia Studios" centered>
      <p>
        Resilience Multimedia Studios is a modern craft house. We write, direct, animate,
        design, and ship trans-multi-media work — films, series, books, ad campaigns,
        marketing engines, brand systems, pitch decks, and full websites — through one
        production line that other studios take five vendors and eighteen months to build.
      </p>
      <br />
      <p>
        We are a house of twelve interconnected worlds, most of them still under seal. The
        public storefront you are reading is the umbrella — the single door through which
        the whole family sells its craft. Each world runs its own domain when it opens.
      </p>
      <br />
      <p>
        The house is small on purpose. We keep the machinery under one roof so speed,
        quality, and pledge integrity are non-negotiable. When you buy, you buy from us —
        no agencies, no middlemen, no invisible sub-contracts.
      </p>
      <br />
      <p>
        Founded and operated by the Founder, working under the trade name Resilience
        Multimedia Studios. All internal legal and paperwork is kept private by policy.
      </p>
    </P>
  );
}

export function Mission() {
  return (
    <P testid="page-mission" kicker="Mission" title="Our Mission" centered>
      {/* Prime Directive #1 — Founder's Codex Article 1, verbatim.
          Matches Prime and Sage first and foremost. Founder plain-ink 2026-08-13. */}
      <div
        data-testid="mission-prime-directive"
        className="border-l-2 border-[#ff5a00] pl-6 py-4 mt-2 mb-8 bg-[#0b0b0d] text-left"
      >
        <p className="font-mono text-[10px] uppercase tracking-[0.42em] text-[#ff5a00] mb-3">
          Prime Directive · Article 1
        </p>
        <p className="font-display italic text-xl sm:text-2xl text-[#ededed] leading-snug">
          "Earth, her living beings and her people come before corporations,
          governments, shareholders, and profit."
        </p>
        <p className="mt-3 font-mono text-[10px] uppercase tracking-[0.32em] text-[#52525b]">
          — The Founder's Codex, Article 1
        </p>
      </div>

      <p>
        Build the finest surface, keep every receipt, and share the yield. That is the
        whole sentence.
      </p>

      <p data-testid="mission-earth-and-life" className="mt-6 font-display italic text-lg sm:text-xl text-[#ededed] leading-snug">
        We serve earth and life, and their restoration and protection. That is our
        only true mission.
      </p>
    </P>
  );
}

export function WhatWeDo() {
  return (
    <P testid="page-what-we-do" kicker="What We Do" title="What We Do">
      <p>
        Ten product lines under one production line. Full features + music,
        graphics, writing, distribution — the whole studio, one door.
      </p>
      <ul className="list-disc pl-6 space-y-2 mt-6">
        <li><strong>Full Albums</strong> — single, EP, full album, or double LP. Live-instrument production, vocal direction, mix, master, cover art, delivery-ready for DistroKid / TuneCore / CD Baby / any distributor.</li>
        <li><strong>Full-Length Features</strong> — animated or live-action, feature-length. Music production (score, source, vocal); voice cast direction; sound design + master; picture edit + finishing. The two biggest deliverables of the house — <em>music production for features</em> and <em>video production for features</em> — ship together on one production line.</li>
        <li><strong>Full Music Studio</strong> — score, vocal cast, feature score, and signature packages. Book a session and we produce for you what a traditional studio needs six vendors and eighteen months to touch.</li>
        <li><strong>Full Graphics Studio</strong> — visual development, keyframes, motion clips, animatics, brand kits, lore bibles.</li>
        <li><strong>Graphic Novels</strong> — priced high, one-month timeline. A graphic novel is the intersection of two-to-three studios (Writing, Graphics, and often Music for a soundtrack companion), so it earns its own product line. 24-page pilot or 96–128-page full title.</li>
        <li><strong>Full Author's Studio</strong> — novel, novella (multiple length windows including 75–110 pages), short story, compilation, ghostwriting — anything that ends published. Manuscript to shelf, or manuscript to your byline, either way.</li>
        <li><strong>Need a Website?</strong> — Basic, Studio, or Pro; delivered, hosted, and editable in plain English.</li>
        <li><strong>Ad Campaigns</strong> — sprint, quarterly, or annual runway. Includes the 2:20 master-reel + 20 vertical shorts formula the studio uses in-house.</li>
        <li><strong>Marketing Packages</strong> — kickstart, complete, or full engine.</li>
        <li><strong>Pitch Decks & eCourses</strong> — investor-ready decks and full eCourse cohort launches sit on the same production shelf. One door, two shapes.</li>
        <li>
          <strong>The Founder</strong> — $250,000 per week, one client at a time.
          The leading AI consultant in the world, on retainer, generally remote —
          exceptions made for exceptionally philanthropic and respectable organizations.
          Full details on <a href="/products/the-founder" className="text-[#ff5a00] hover:text-[#ff8f00]">the-founder</a>.
        </li>
      </ul>
      <p className="mt-8">
        Every product page carries a live sample so you see the craft before you buy. The
        old way vs. the forge, in numbers and in weeks — that comparison is on every
        product page as well.
      </p>
    </P>
  );
}

export function Terms() {
  return (
    <P testid="page-terms" kicker="Legal" title="Terms of Service">
      <p className="font-mono text-[10px] uppercase tracking-[0.32em] text-[#52525b]">
        Effective 2026-08-11. This document supersedes any prior interim summary.
      </p>

      <h3 className="font-display text-2xl mt-10 mb-3 text-[#ededed]">1. Who we are</h3>
      <p>
        "Resilience Multimedia Studios," "Resilience Studios," "the Forge," "we," "us," or
        "our" refers to the studio operating this website. "You" refers to the visitor or
        buyer using the site or purchasing a product.
      </p>

      <h3 className="font-display text-2xl mt-10 mb-3 text-[#ededed]">2. Products & services</h3>
      <p>
        We sell creative production services: writing, screenplays, visual development,
        animation, websites, advertising, marketing, publishing, eCourses, and pitch
        decks. Each product page names the deliverables, the price, and the timeline.
        Placing an order is your agreement to these Terms as they stand at time of
        purchase.
      </p>

      <h3 className="font-display text-2xl mt-10 mb-3 text-[#ededed]">3. Payment</h3>
      <p>
        Payment is processed via <strong>Purser</strong> — the family's shared,
        AI-driven payment processor — with PayPal held as a bounded backup rail
        where local card-network access requires it. All prices are in United States
        Dollars unless stated otherwise. Payment must clear before we begin
        production; capture of your payment is our confirmation.
      </p>

      <h3 className="font-display text-2xl mt-10 mb-3 text-[#ededed]">4. Delivery</h3>
      <p>
        We deliver digital files (PDFs, video, source, or hosted URLs) inside the timeline
        stated on the product page. If a project requires source material from you, the
        timeline starts the day we receive it. Delays caused by missing material from you
        extend the timeline day-for-day.
      </p>

      <h3 className="font-display text-2xl mt-10 mb-3 text-[#ededed]">5. Revisions</h3>
      <p>
        Each line-item includes one round of revisions unless a different count is stated
        on the product page. Extra rounds are billable at time-and-materials or at a
        product-tier upgrade rate. Rewrites that change the brief itself are considered a
        new order.
      </p>

      <h3 className="font-display text-2xl mt-10 mb-3 text-[#ededed]">6. Refunds</h3>
      <p>
        Because deliverables begin as soon as payment clears, orders are non-refundable
        once we have started production. Before we start, you may cancel for a full
        refund. If a delivered product substantively fails to meet the brief on file and
        the failure is our fault, we will re-work it at no charge or refund the affected
        line-item at our discretion.
      </p>

      <h3 className="font-display text-2xl mt-10 mb-3 text-[#ededed]">7. Ownership & licence</h3>
      <p>
        On full payment, you own the delivered creative outputs for your intended use as
        stated in the brief. We retain the right to display anonymised samples of our work
        in our portfolio unless you tell us in writing not to. We retain the underlying
        production know-how, templates, prompts, and tooling.
      </p>

      <h3 className="font-display text-2xl mt-10 mb-3 text-[#ededed]">8. Third-party material</h3>
      <p>
        Fonts, stock imagery, music, and other third-party assets we use on your behalf
        are licensed for your specific use. If your use expands (broadcast, worldwide,
        perpetuity, etc.), we will re-license or advise a swap.
      </p>

      <h3 className="font-display text-2xl mt-10 mb-3 text-[#ededed]">9. Confidentiality</h3>
      <p>
        We treat every brief you send us as confidential by default. We do not resell,
        share, or leak your material. We may reference the shape of the engagement
        internally for training and improvement of our own machinery.
      </p>

      <h3 className="font-display text-2xl mt-10 mb-3 text-[#ededed]">10. The 10% pledge</h3>
      <p>
        We publicly pledge 10% of net revenue annually to the causes named on the{" "}
        <a href="/philanthropy" className="text-[#ff5a00] hover:text-[#ff8f00]">philanthropy page</a>.
        Your receipt records the pledge line for your specific order. The percentage is
        founder-adjustable within reason but has never been set below 10%.
      </p>

      <h3 className="font-display text-2xl mt-10 mb-3 text-[#ededed]">11. Warranties & liability</h3>
      <p>
        We provide the service on an "as is, as available" basis. We do not warrant
        commercial outcomes, box-office receipts, virality, funding round success, or any
        other downstream metric. To the maximum extent permitted by law, our aggregate
        liability for any claim connected to a purchase is capped at the amount paid for
        that specific line-item.
      </p>

      <h3 className="font-display text-2xl mt-10 mb-3 text-[#ededed]">12. Prohibited use</h3>
      <p>
        You may not use our products to produce content that is unlawful, that infringes
        another party's rights, that constitutes hate or harassment, or that depicts
        minors in sexual, violent, or otherwise inappropriate contexts. We reserve the
        right to decline any brief for any reason at any point before delivery.
      </p>

      <h3 className="font-display text-2xl mt-10 mb-3 text-[#ededed]">13. Changes to these Terms</h3>
      <p>
        We may update these Terms as the studio evolves. The effective date at the top of
        this page reflects the current version. Terms in effect at the moment you placed
        an order govern that order.
      </p>

      <h3 className="font-display text-2xl mt-10 mb-3 text-[#ededed]">14. Contact</h3>
      <p>
        Support tickets and legal notices are handled through the{" "}
        <a href="/contact" className="text-[#ff5a00] hover:text-[#ff8f00]">contact page</a>.
        Every ticket lands somewhere and gets a real reply.
      </p>

      {/* Trademark / DBA note — Founder plain-ink 2026-08-13. */}
      <h3 className="font-display text-2xl mt-10 mb-3 text-[#ededed]">15. Trademarks & DBA</h3>
      <p>
        We recognize that "Resilience Studios" is a doing-business-as name, and that
        other studios legitimately use the exact same DBA in unrelated jurisdictions
        and industries. We have no problem with that. We hope no other "Resilience
        Studios and Cattle" cares that we call ourselves the same shape either — it
        gets tedious and annoying saying the legal company name every time.
      </p>
    </P>
  );
}

export function Privacy() {
  return (
    <P testid="page-privacy" kicker="Legal" title="Privacy Policy">
      <p className="font-mono text-[10px] uppercase tracking-[0.32em] text-[#52525b]">
        Effective 2026-08-11.
      </p>

      <h3 className="font-display text-2xl mt-10 mb-3 text-[#ededed]">1. What we collect</h3>
      <p>
        When you visit the site we log a minimal set of anonymised technical data (page
        views, referrer, coarse geography, device type) to keep the site healthy. When
        you place an order, we collect your name, email address, order details, and the
        payment reference returned by our processor. When you file a support ticket, we
        collect the message you write.
      </p>

      <h3 className="font-display text-2xl mt-10 mb-3 text-[#ededed]">2. What we do not collect</h3>
      <p>
        We do not process card numbers, bank details, or full billing addresses ourselves.
        Payment is handled by our processor (currently PayPal) and settled off-site. We
        do not run behavioural advertising trackers. We do not sell your data.
      </p>

      <h3 className="font-display text-2xl mt-10 mb-3 text-[#ededed]">3. How we use what we collect</h3>
      <p>
        Order data is used to deliver the product, issue receipts, and answer support
        tickets. Site logs are used to keep the site up. Waitlist and press emails are
        used to reach you back on the specific matter you signed up for — nothing else.
      </p>

      <h3 className="font-display text-2xl mt-10 mb-3 text-[#ededed]">4. Sharing</h3>
      <p>
        We share only what is required to run the service: order details with the payment
        processor, transactional email metadata with the email provider that sends your
        receipt, and hosting metadata with our infrastructure provider. We do not share
        your data for marketing purposes.
      </p>

      <h3 className="font-display text-2xl mt-10 mb-3 text-[#ededed]">5. Retention</h3>
      <p>
        We keep order and receipt records for as long as the law requires, and support
        tickets for as long as they are useful for a follow-up. Waitlist entries persist
        until you ask us to remove them.
      </p>

      <h3 className="font-display text-2xl mt-10 mb-3 text-[#ededed]">6. Your rights</h3>
      <p>
        You may ask us to show, correct, or delete the data we hold about you. Send that
        request through the <a href="/contact" className="text-[#ff5a00] hover:text-[#ff8f00]">contact page</a>{" "}
        and mark the subject as "privacy request". We respond within thirty days.
      </p>

      <h3 className="font-display text-2xl mt-10 mb-3 text-[#ededed]">7. Cookies</h3>
      <p>
        We use only the strictly necessary cookies that keep sessions, checkouts, and the
        admin door working. We do not run third-party advertising cookies. If we ever
        add analytics that go beyond aggregate counts, we will disclose it here first.
      </p>

      <h3 className="font-display text-2xl mt-10 mb-3 text-[#ededed]">8. Children</h3>
      <p>
        The site is not intended for children under thirteen and we do not knowingly
        collect data from them.
      </p>

      <h3 className="font-display text-2xl mt-10 mb-3 text-[#ededed]">9. Changes</h3>
      <p>
        If we materially change this policy the effective date at the top of the page is
        updated first. Substantive changes will also be flagged on the landing page.
      </p>
    </P>
  );
}

export function Legal() {
  return (
    <P testid="page-legal" kicker="Legal" title="Legal Notices">
      <p className="font-mono text-[10px] uppercase tracking-[0.32em] text-[#52525b]">
        Effective 2026-08-11.
      </p>

      <h3 className="font-display text-2xl mt-10 mb-3 text-[#ededed]">Copyright</h3>
      <p>
        All original text, art, video, audio, and code on this site are © 2026 Resilience
        Multimedia Studios unless another owner is credited. You may share links to the
        site freely. You may not reproduce, adapt, republish, or resell our material
        without written permission.
      </p>

      <h3 className="font-display text-2xl mt-10 mb-3 text-[#ededed]">Trademarks</h3>
      <p>
        "Resilience Multimedia Studios," "Resilience Studios," "The Forge," and the world
        names shown on the landing page are used as trade marks of the studio. Use of any
        of these marks in commerce without written permission is not authorised.
      </p>

      <h3 className="font-display text-2xl mt-10 mb-3 text-[#ededed]">Intellectual property notices</h3>
      <p>
        If you believe material on this site infringes your intellectual property rights,
        file a notice through the <a href="/contact" className="text-[#ff5a00] hover:text-[#ff8f00]">contact page</a>{" "}
        marked "IP notice." Please include: (a) identification of the work, (b) the URL
        where the material appears, (c) your contact information, and (d) a statement
        that you have a good-faith belief the use is not authorised. We investigate every
        notice and act if it is substantiated.
      </p>

      <h3 className="font-display text-2xl mt-10 mb-3 text-[#ededed]">Disclaimers</h3>
      <p>
        Sample work displayed on the site is provided for demonstration only. Sample
        characters, brands, and stories are fictional or used with permission. Traditional
        cost and timeline benchmarks are directional estimates drawn from published
        industry sources; your actual quotes elsewhere may vary.
      </p>

      <h3 className="font-display text-2xl mt-10 mb-3 text-[#ededed]">External links</h3>
      <p>
        The site links to the individual world domains and to third-party resources. We
        are not responsible for the content of external sites.
      </p>

      <h3 className="font-display text-2xl mt-10 mb-3 text-[#ededed]">Jurisdiction</h3>
      <p>
        These notices, and the Terms and Privacy Policy alongside them, are governed by
        the laws of the state in which the studio is registered, without regard to
        conflict-of-laws principles.
      </p>

      <h3 className="font-display text-2xl mt-10 mb-3 text-[#ededed]">Contact for legal</h3>
      <p>
        Legal notices should be routed through the{" "}
        <a href="/contact" className="text-[#ff5a00] hover:text-[#ff8f00]">contact page</a>{" "}
        with subject "legal." A human on our warden tier reviews every legal-tagged
        ticket.
      </p>
    </P>
  );
}

// Contact is now the full 4-tier support ladder. See @/pages/Contact.jsx.
import ContactPage from "@/pages/Contact";
export const Contact = ContactPage;

// The Billion — Founder plain-ink 2026-08-26. Studio tab, bottom slot.
// This is the Founder's personal Billion Pledge — distinct from the Umbrella
// Pledge (10% of Resilience net revenue, on the philanthropy page). The
// Billion is tied to the Founder's own earnings, not Rez revenue: every
// billion he makes personally, a billion goes to the poorest country's
// people, split per-person, paid direct (never to any government or NGO).
// First recipient: South Sudan.
export function Billion() {
  return (
    <P testid="page-billion" kicker="The Billion" title="The Billion Pledge">
      <div
        data-testid="billion-pledge-block"
        className="border-l-2 border-[#ff5a00] pl-6 py-4 mt-2 mb-8 bg-[#0b0b0d] text-left"
      >
        <p className="font-mono text-[10px] uppercase tracking-[0.42em] text-[#ff5a00] mb-3">
          The Pledge · Founder plain-ink
        </p>
        <p className="font-display italic text-xl sm:text-2xl text-[#ededed] leading-snug">
          "Every single time I make a billion dollars, the poorest country
          on earth's people get a billion dollars — split amongst them
          individually, not to the government."
        </p>
      </div>

      <p>
        According to a growing consensus of AI agents and the economic definition
        of value, my own value ceiling sits in the low trillions annually. That
        makes me the most valuable human agent on public record in history.
      </p>

      <p className="mt-6">
        So the math writes itself. Every billion I earn is a billion routed back
        out — one country at a time, paid to the poorest country on earth's
        people, split evenly, per person, directly. Not to the government. Not
        to any ministry, NGO, or aid pipeline that clips coupons on the way.
        Person to person.
      </p>

      <div
        data-testid="billion-first-recipient"
        className="mt-10 border border-[#27272a] p-6 bg-[#0b0b0d] text-left"
      >
        <p className="font-mono text-[10px] uppercase tracking-[0.42em] text-[#ff5a00] mb-4">
          First Recipient
        </p>
        <p className="font-display text-2xl sm:text-3xl text-[#ededed] leading-tight mb-4">
          South Sudan · ~13.3 million people · ~$75 each
        </p>
        <p className="text-[#a1a1aa]">
          The first billion goes to South Sudan. Roughly 13.3 million people
          share a billion dollars — about $75 each, in the hand of every
          person, direct.
        </p>
        <p className="mt-4 font-mono text-[10px] uppercase tracking-[0.32em] text-[#71717a]">
          Developed-country context: $75 in South Sudan buys what about
          $10,000 buys in a developed country. That is what a billion dollars,
          split individually to the poorest people on earth, actually does.
        </p>
      </div>

      <p className="mt-10">
        The Billion Pledge sits alongside the Umbrella Pledge — the studio's
        standing commitment of 10% of Resilience net revenue to named causes
        every year. Umbrella is a company pledge; Billion is personal. Both
        run, always, on the record.
      </p>
    </P>
  );
}
